import React, { useRef, useContext, useEffect } from 'react'

import { useForm } from 'react-hook-form'
import DfbButton from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_button'
import DfbHeadline from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_headline'
import DfbSymbol from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_symbol'
import DfbParagraph from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_paragraph'
import PageContext from '@dfb/gatsby-dfb-theme/src/components/page-context'
import { useCaptcha } from '../../../../../utils/form/useCaptcha'
import { getFormDataDefinition } from './formData'

const MDefaultform = ({
  buttonLabel,
  children,
  disableCaptcha,
  DOImail,
  footer,
  formType,
  identifier,
  onSubmit,
  postUrl,
  shouldPersist,
}) => {
  const ref = useRef(null)
  const { pageUid } = useContext(PageContext)

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, isSubmitSuccessful, errors },
    setError,
    watch,
    getValues,
    resetField,
  } = useForm({
    mode: 'onSubmit',
    shouldUnregister: true,
  })

  const resultText = useRef(null)

  useEffect(() => {
    if (isSubmitSuccessful && resultText.current) {
      // eslint-disable-next-line no-undef
      window.scrollTo({ top: resultText.current.getBoundingClientRect().top + window.pageYOffset - 150, behavior: 'instant' })
    }
  }, [isSubmitSuccessful])

  const captchaHook = useCaptcha()

  const defaultHandleSubmit = async (body) => {
    const requestParams = {
      method: 'POST',
      body: body,
    }

    if (!disableCaptcha) {
      requestParams['headers'] = {
        solution: captchaHook.captchaStatus.solution,
      }
    }
    const response = await fetch(postUrl, requestParams)

    if (response?.status < 200 || response?.status >= 300) {
      console.error(response?.status)
      throw new Error(`POST call to ${process.env.FORM_API_URL} not successful. Returned status ${response.status}`)
    }
  }

  const transformSelection = (formType, value) => {
    let bool = value === 'true' ? true : false
    switch (formType) {
      case 'CupderFansEinzelanmeldung':
      case 'CupDerFansGruppenanmeldung':
      case 'Fanbelange':
      case 'Fuballhelden':
      case 'DFBSchiedsrichterzeitung':
        return bool === true ? 'akzeptiert' : 'abgelehnt'
      default:
        return bool.toString()
    }
  }

  const onSubmitValidForm = async (data) => {
    const formData = getFormDataDefinition(formType)
    formData.content.id = pageUid
    formData.content.formType = formType
    formData.content.values.recipient = identifier
    formData.content.values.email = DOImail ? DOImail : ''
    formData.content.values.saveInDb = shouldPersist ? 'true' : 'false'
    // Convert Boolean values to strings, as the form API expects strings
    Object.keys(data).forEach((item) => {
      if (typeof data[item] === 'boolean' || data[item] === 'true' || data[item] === 'false') {
        data[item] = transformSelection(formType, data[item].toString())
      }
      //Write each single item to formData object because if the whole data-object is passed to formData the email is magically removed
      formData.content.values[item] = data[item]
    })

    const finalFormdata = new FormData()
    Object.entries(formData).forEach(([key, value]) => {
      finalFormdata.append(key, JSON.stringify(value))
    })

    if (!disableCaptcha && !captchaHook.captchaStatus.solution) {
      setError('root.captcha', {
        type: 'manual',
        message: 'Bitte bestätigen, dass Du kein Roboter bist.',
      })
      return
    }

    try {
      if (onSubmit) {
        await onSubmit(finalFormdata)
      } else {
        await defaultHandleSubmit(finalFormdata)
      }

      ref.current.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: 'smooth',
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Unable to submit form', error)

      setError('root.submit', {
        type: 'manual',
        message: 'Leider ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.',
      })
    }
  }

  if (isSubmitSuccessful) {
    return (
      <center ref={resultText} style={{ margin: '0 auto' }}>
        <div style={{ height: '64px', width: '64px', marginBottom: '24px' }}>
          <DfbSymbol icon_type="check-circle" />
        </div>
        <DfbHeadline headline="Deine Anfrage wurde erfolgreich übermittelt!" headline_level="h4" />
        <DfbParagraph text="Der Ball liegt jetzt bei uns und wir kümmern uns." />
      </center>
    )
  }

  return (
    <form className="m-Formblock-form" onSubmit={handleSubmit(onSubmitValidForm)} ref={ref}>
      {children({ register, errors, watch, setError, captchaHook, getValues, resetField })}
      {!disableCaptcha && (
        <>
          <div className="m-Formblock-input-half">
            <div className="m-Formblock-input">
              {captchaHook.CaptchaWidget({
                button: {
                  color: 'inherit',
                },
              })}
            </div>
            <div className="m-Formblock-input"></div>
          </div>

          {errors?.root?.captcha && (
            <div className="dfb-Input is-invalid">
              <DfbParagraph add_class="dfb-Paragraph--small dfb-Input-errorMessage" text={errors.root.captcha.message} />
            </div>
          )}
        </>
      )}

      {errors?.root?.submit && (
        <div className="dfb-Input is-invalid">
          <DfbParagraph add_class="dfb-Paragraph--small dfb-Input-errorMessage" text={errors.root.submit.message} />
        </div>
      )}

      {errors?.root && <div className="m-Formblock-spacer" />}

      <DfbButton text={buttonLabel} type="submit" disabled={isSubmitting} />
      {footer}
    </form>
  )
}

export default MDefaultform
